import React, { useState, useEffect, useMemo } from 'react';
import { FaSearch, FaPlus, FaEdit, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import Swal from 'sweetalert2';
import api from '../../../../utils/api';
import useColleges from '../../../../utils/useColleges';
import { useAuth } from '../../../../context/AuthContext';
import './Buses.css';

const Buses = () => {
  const { user } = useAuth();
  const { colleges, loading: collegesLoading } = useColleges();
  const isScoped = user?.role === 'collegeadmin' || user?.role === 'clerk';

  const [busesList, setBusesList] = useState([]);
  const [routesList, setRoutesList] = useState([]);
  const [allocationsList, setAllocationsList] = useState([]); // Added to track filled seats
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Search & Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCollege, setFilterCollege] = useState('');
  const [filterRoute, setFilterRoute] = useState('');
  
  // UI State
  const [showAddForm, setShowAddForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  // Form State
  const [formCollege, setFormCollege] = useState('');
  const [formRoute, setFormRoute] = useState('');
  const [formNumber, setFormNumber] = useState('');
  const [formCapacity, setFormCapacity] = useState('');
  const [formDriver, setFormDriver] = useState('');
  const [formPhone, setFormPhone] = useState('');

  useEffect(() => {
    if (colleges.length === 1) {
      setFormCollege(colleges[0]._id);
      if (!isScoped) setFilterCollege(colleges[0]._id);
    }
  }, [colleges, isScoped]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [busesRes, routesRes, allocRes] = await Promise.all([
        api.get('/transport/buses'),
        api.get('/transport/routes'),
        api.get('/transport/allocations') // Fetch allocations to compute available seats
      ]);
      setBusesList(busesRes.data.data || []);
      setRoutesList(routesRes.data.data || []);
      setAllocationsList(allocRes.data.data || []);
    } catch {
      setBusesList([]);
      setRoutesList([]);
      setAllocationsList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const resetForm = () => {
    setFormCollege(colleges.length === 1 ? colleges[0]._id : '');
    setFormRoute('');
    setFormNumber('');
    setFormCapacity('');
    setFormDriver('');
    setFormPhone('');
    setEditId(null);
    setShowAddForm(false);
  };

  const handleSubmit = async () => {
    if (!formCollege || !formRoute || !formNumber.trim() || !formCapacity || !formDriver.trim()) {
      Swal.fire('Error', 'Please fill all required fields.', 'warning');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        college: formCollege, route: formRoute, busNumber: formNumber, 
        capacity: Number(formCapacity), driverName: formDriver, 
        driverPhone: formPhone, status: 'Active' // Status hidden from form
      };
      
      if (editId) {
        await api.put(`/transport/buses/${editId}`, payload);
        Swal.fire('Updated', 'Bus updated successfully', 'success');
      } else {
        await api.post('/transport/buses', payload);
        Swal.fire('Added', 'Bus added successfully', 'success');
      }
      resetForm();
      loadData();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (bus) => {
    setEditId(bus._id);
    setFormCollege(bus.college?._id || bus.college || '');
    setFormRoute(bus.route?._id || bus.route || '');
    setFormNumber(bus.busNumber);
    setFormCapacity(bus.capacity);
    setFormDriver(bus.driverName);
    setFormPhone(bus.driverPhone || '');
    setShowAddForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleStatus = async (bus) => {
    try {
      const newStatus = bus.status === 'Active' ? 'Inactive' : 'Active';
      // amazonq-ignore-next-line
      await api.put(`/transport/buses/${bus._id}`, { 
        ...bus, 
        college: bus.college?._id || bus.college,
        route: bus.route?._id || bus.route,
        status: newStatus 
      });
      loadData();
    } catch {
      Swal.fire('Error', 'Failed to update status', 'error');
    }
  };

  const formRoutesOptions = useMemo(() => {
    if (!formCollege) return [];
    // Only Active routes in the form dropdown
    return routesList.filter(r =>
      String(r.college?._id || r.college) === String(formCollege) && r.status === 'Active'
    );
  }, [routesList, formCollege]);

  const handleRouteChange = (newRouteId) => {
    if (editId && formRoute && newRouteId !== formRoute) {
      Swal.fire({
        icon: 'warning',
        title: 'Route Change Warning',
        text: 'Changing the route will NOT update existing student allocations on this bus. Students will still show their old route until manually updated.',
        confirmButtonText: 'I Understand, Change Route',
        showCancelButton: true,
      }).then(r => { if (r.isConfirmed) setFormRoute(newRouteId); });
    } else {
      setFormRoute(newRouteId);
    }
  };

  const filterRouteOptions = useMemo(() => {
    let available = routesList;
    if (filterCollege) {
      available = available.filter(r => String(r.college?._id || r.college) === filterCollege);
    }
    return available;
  }, [routesList, filterCollege]);

  const displayedBuses = useMemo(() => {
    return busesList.filter(b => {
      const q = searchQuery.toLowerCase();
      const matchSearch = b.busNumber.toLowerCase().includes(q) || b.driverName.toLowerCase().includes(q);
      const matchCollege = filterCollege ? String(b.college?._id || b.college) === filterCollege : true;
      const matchRoute = filterRoute ? String(b.route?._id || b.route) === filterRoute : true;
      return matchSearch && matchCollege && matchRoute;
    });
  }, [busesList, searchQuery, filterCollege, filterRoute]);

  const groupedBuses = useMemo(() => {
    const g = {};
    displayedBuses.forEach(bus => {
      const collegeId = bus.college?._id || bus.college;
      const found = colleges.find(c => String(c._id) === String(collegeId));
      const key = found?.name || bus.college?.name || 'Unassigned';
      if (!g[key]) g[key] = [];
      
      // Calculate available seats
      const allocatedCount = allocationsList.filter(a => String(a.bus?._id || a.bus) === String(bus._id)).length;
      const availableSeats = Math.max(0, bus.capacity - allocatedCount);
      
      g[key].push({ ...bus, allocatedCount, availableSeats });
    });
    return g;
  }, [displayedBuses, colleges, allocationsList]);

  return (
    <div className="buses-for-transport-student-container">
      {/* Controls */}
      <div className="buses-for-transport-student-controls">
        <div className="buses-for-transport-student-search-wrapper">
          <FaSearch className="buses-for-transport-student-search-icon" />
          <input type="text" className="buses-for-transport-student-search-input"
            placeholder="Search Bus No. or Driver..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>

        {!isScoped && colleges.length > 1 && (
          <select className="buses-for-transport-student-filter-select" value={filterCollege} onChange={e => { setFilterCollege(e.target.value); setFilterRoute(''); }}>
            <option value="">All Colleges</option>
            {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        )}

        <select className="buses-for-transport-student-filter-select" value={filterRoute} onChange={e => setFilterRoute(e.target.value)}>
          <option value="">All Routes</option>
          {filterRouteOptions.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
        </select>

        <button className="buses-for-transport-student-btn-add" onClick={() => { if (showAddForm) resetForm(); else setShowAddForm(true); }}>
          <FaPlus /> {showAddForm ? 'Close Form' : 'Add Bus'}
        </button>
      </div>

      {/* Form */}
      {showAddForm && (
        <div className="buses-for-transport-student-form">
          <h3 className="buses-for-transport-student-form-title">{editId ? '✏️ Edit Bus' : 'Add New Bus'}</h3>
          <div className="buses-for-transport-student-form-grid">
            <div className="buses-for-transport-student-form-group">
              <label>College <span className="buses-for-transport-student-req">*</span></label>
              <select value={formCollege} onChange={e => { setFormCollege(e.target.value); setFormRoute(''); }} disabled={isScoped && colleges.length === 1}>
                <option value="">-- Select College --</option>
                {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
            <div className="buses-for-transport-student-form-group">
              <label>Assign Route <span className="buses-for-transport-student-req">*</span></label>
              <select value={formRoute} onChange={e => handleRouteChange(e.target.value)} disabled={!formCollege}>
                <option value="">-- Select Route --</option>
                {formRoutesOptions.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
              </select>
            </div>
            <div className="buses-for-transport-student-form-group">
              <label>Bus Number <span className="buses-for-transport-student-req">*</span></label>
              <input type="text" value={formNumber} onChange={e => setFormNumber(e.target.value)} placeholder="e.g. MH 12 AB 1234" />
            </div>
            <div className="buses-for-transport-student-form-group">
              <label>Total Capacity <span className="buses-for-transport-student-req">*</span></label>
              <input type="number" value={formCapacity} onChange={e => setFormCapacity(e.target.value)} placeholder="e.g. 50" min="1" />
            </div>
            <div className="buses-for-transport-student-form-group">
              <label>Driver Name <span className="buses-for-transport-student-req">*</span></label>
              <input type="text" value={formDriver} onChange={e => setFormDriver(e.target.value)} placeholder="Driver Name" />
            </div>
            <div className="buses-for-transport-student-form-group">
              <label>Driver Phone</label>
              <input type="text" value={formPhone} onChange={e => setFormPhone(e.target.value)} placeholder="Phone Number" />
            </div>
          </div>
          <div className="buses-for-transport-student-form-buttons">
            <button className="buses-for-transport-student-btn-cancel" onClick={resetForm} disabled={saving}>Cancel</button>
            <button className="buses-for-transport-student-btn-confirm" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : editId ? 'Update Bus' : 'Save Bus'}
            </button>
          </div>
        </div>
      )}

      {/* Grouped Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading buses...</div>
      ) : Object.keys(groupedBuses).length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b', background: '#fff', borderRadius: '8px' }}>No buses found.</div>
      ) : (
        <div className="buses-for-transport-student-grouped-container">
          {Object.entries(groupedBuses).map(([collegeName, buses]) => (
            <div key={collegeName} className="buses-for-transport-student-table-group">
              <h3 className="buses-for-transport-student-group-header">🏫 {collegeName}</h3>
              <div className="buses-for-transport-student-table-wrapper">
                <table className="buses-for-transport-student-table">
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th className="buses-for-transport-student-align-left">Bus Number</th>
                      <th className="bus-col-hide">Route</th>
                      <th>Seats</th>
                      <th className="bus-col-hide">Driver</th>
                      <th className="bus-col-hide">Phone</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {buses.map((bus, i) => {
                      const isFull = bus.availableSeats <= 0;
                      return (
                        <React.Fragment key={bus._id}>
                          <tr className="buses-for-transport-student-main-row">
                            <td>{i + 1}</td>
                            <td className="buses-for-transport-student-align-left">
                              <div className="buses-for-transport-student-name-wrapper" onClick={() => setExpandedRow(expandedRow === bus._id ? null : bus._id)}>
                                <span style={{ fontWeight: 700 }}>{bus.busNumber}</span>
                                <button className="buses-for-transport-student-expand-btn">
                                  {expandedRow === bus._id ? <FaChevronUp /> : <FaChevronDown />}
                                </button>
                              </div>
                            </td>
                            <td className="bus-col-hide">
                              <span className="buses-for-transport-student-route-badge">{bus.route?.name || '—'}</span>
                            </td>
                            <td>
                              <div className="buses-for-transport-student-seats-wrapper">
                                <span className="seats-filled" style={{ color: isFull ? '#e74c3c' : '#1e293b', fontWeight: 700 }}>{bus.allocatedCount}</span>
                                <span className="seats-sep">/</span>
                                <span className="seats-total">{bus.capacity}</span>
                                {isFull
                                  ? <span className="buses-for-transport-student-full-badge">FULL</span>
                                  : <span className="seats-remaining" style={{ color: '#059669' }}>({bus.availableSeats} left)</span>
                                }
                              </div>
                            </td>
                            <td className="bus-col-hide">{bus.driverName}</td>
                            <td className="bus-col-hide">{bus.driverPhone || '—'}</td>
                            <td>
                              <label className="buses-for-transport-student-switch">
                                <input type="checkbox" checked={bus.status === 'Active'} onChange={() => toggleStatus(bus)} />
                                <span className="buses-for-transport-student-slider" />
                              </label>
                              <div style={{ fontSize: '0.7rem', marginTop: 4, color: bus.status === 'Active' ? '#27ae60' : '#e74c3c', fontWeight: 600 }}>
                                {bus.status || 'Active'}
                              </div>
                            </td>
                            <td>
                              <div className="buses-for-transport-student-action-btns">
                                <button className="buses-for-transport-student-btn-edit" onClick={() => handleEdit(bus)} title="Edit"><FaEdit /> Edit</button>
                              </div>
                            </td>
                          </tr>

                          {/* Expanded Row for Mobile Views */}
                          {expandedRow === bus._id && (
                            <tr className="buses-for-transport-student-expanded-row">
                              <td colSpan="8">
                                <div className="buses-for-transport-student-expanded-content">
                                  <div className="bus-exp-item bus-exp-mobile-only">
                                    <span className="bus-exp-label">Route</span>
                                    <span className="bus-exp-value">{bus.route?.name || '—'}</span>
                                  </div>
                                  <div className="bus-exp-item bus-exp-mobile-only">
                                    <span className="bus-exp-label">Driver Name</span>
                                    <span className="bus-exp-value">{bus.driverName}</span>
                                  </div>
                                  <div className="bus-exp-item bus-exp-mobile-only">
                                    <span className="bus-exp-label">Driver Phone</span>
                                    <span className="bus-exp-value">{bus.driverPhone || '—'}</span>
                                  </div>
                                  <div className="bus-exp-item">
                                    <span className="bus-exp-label">Allocated vs Capacity</span>
                                    <span className="bus-exp-value" style={{color: isFull ? '#e74c3c' : '#1e293b'}}>
                                      {bus.allocatedCount} allocated out of {bus.capacity} 
                                      {isFull ? ' (Bus is Full)' : ''}
                                    </span>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Buses;